import React, { useState } from "react";
import Collapse from "../../../components/Collapse";

/**
 * Bet table for Queen Top Open (teen41) / Jack Top Open (teen42) Teenpatti in the reference markup
 * (desktop / isTablet template):
 *   .teen1daycasino-container.d-none-small
 *     > .teen1dayleft (Player A: title row + "Main" back/lay row)
 *     > .teen1daycenter
 *     > .teen1dayright (Player B: title row + "Main" back/lay row)
 *     > .teen2uo > (.teen1dayleft, .teen1daycenter, .teen1dayright: Player B Under 21 / Over 21 back-only)
 * Suspended = gstatus !== "OPEN" (reference). Book: .book-green when > 0, otherwise .book-red.
 *
 * Props: main = [Player A, Player B] (subtype teen41/teen42, by sr), underover = [Under 21, Over 21]
 * (subtype uo, by sr). onBetClick(value, label, type, item) is called with the same labels as before
 * ("Box A - Main", "Box B - Main", "Player B Under 21", "Player B Over 21").
 */

// v-kvalpoint / v-kval: >= 1000 → "200K" / "50K", else the plain number
const kval = (size, digits) => {
  const n = Number(size);
  if (!Number.isFinite(n)) return "";
  return n >= 1e3 ? `${parseFloat((n / 1e3).toFixed(digits))}K` : `${parseFloat(n)}`;
};

// toCurrency (EN-IN, max 2 decimals); 0 stays "0"
const toCurrency = (v) => (v ? parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : 0);

const normalize = (s) =>
  String(s || "")
    .replace(/\s*\(\s*/g, "(")
    .replace(/\s*\)\s*/g, ")")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export default function BetTableTeenPatti3({ main = [], underover = [], onBetClick, exposures = {}, myBets = [] }) {
  const [openRanges, setOpenRanges] = useState({});
  const toggleRange = (id) => setOpenRanges((prev) => ({ ...prev, [id]: !prev[id] }));

  const lookup = (keys) => {
    for (const k of keys) {
      if (!k) continue;
      for (const key of [k, k.toLowerCase(), k.trim(), normalize(k)]) {
        if (exposures[key] !== undefined && exposures[key] !== 0) return parseFloat(exposures[key]) || 0;
      }
    }
    return 0;
  };

  // Main market book: exposure map (team_name) first, then the matching open bet's exposer
  const mainBook = (item, box) => {
    const nat = item?.nat || (box === "A" ? "Player A" : "Player B");
    const fromMap = lookup([`${nat} Main`, `Box ${box} - Main`, nat]);
    if (fromMap) return fromMap;
    const bet = myBets.find((b) => {
      const sel = String(b.matchedBet || b.selection || b.player_name || "").toLowerCase();
      return (sel.includes(nat.toLowerCase()) && sel.includes("main")) || sel.includes(`box ${box.toLowerCase()}`) || sel === nat.toLowerCase();
    });
    return bet && bet.exposer ? parseFloat(bet.exposer) || 0 : 0;
  };

  const uoBook = (item) => lookup([item?.nat]);

  const isOpen = (item) => String(item?.gstatus || "").toUpperCase() === "OPEN";

  const rangeIcon = (id, min, max, digits, indent) => (
    <>
      <i
        className={openRanges[id] ? "fas fa-info-circle" : "fas fa-info-circle collapsed"}
        data-toggle="collapse"
        data-target={`#${id}`}
        aria-expanded={openRanges[id] ? "true" : "false"}
        onClick={() => toggleRange(id)}
      />{" "}
      <Collapse id={id} open={!!openRanges[id]} className="icon-range">
        {indent}R:<span>{kval(min, digits)}</span>-<span>{kval(max, digits)}</span>
      </Collapse>
    </>
  );

  const titleRow = (playerClass, label, withBackLay = true) => (
    <div className="casino-box-row">
      <div className="casino-nation-name no-border casino-bl-box-title">
        <div className={playerClass}>{label}</div>
      </div>{" "}
      <div className="casino-bl-box casino-bl-box-title">
        {withBackLay && (
          <>
            <div className="casino-bl-box-item">
              <b>Back</b>
            </div>{" "}
            <div className="casino-bl-box-item">
              <b>Lay</b>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const mainRow = (item, box, rangeId) => {
    const open = isOpen(item);
    const book = mainBook(item, box);
    const click = (value, type) => {
      if (!open || !value) return;
      onBetClick?.(value, `Box ${box} - Main`, type, item);
    };
    return (
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <b>Main</b>{" "}
          <div className="float-right">
            <span className={`mr-2 casino-book ${book > 0 ? "book-green" : "book-red"}`}>{toCurrency(book)}</span>{" "}
            {rangeIcon(rangeId, item?.min, item?.max, 2, "\n                          ")}
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div className={`back casino-bl-box-item${open ? "" : " suspended"}`} onClick={() => click(item?.b, "back")}>
            <span className="casino-box-odd">{item?.b ?? ""}</span>
          </div>{" "}
          <div className={`lay casino-bl-box-item${open ? "" : " suspended"}`} onClick={() => click(item?.l, "lay")}>
            <span className="casino-box-odd">{item?.l ?? ""}</span>
          </div>
        </div>
      </div>
    );
  };

  const uoCells = (item, rangeId) => {
    const open = isOpen(item);
    const book = uoBook(item);
    return (
      <>
        <div className="casino-nation-name">
          <b>{item?.nat}</b>{" "}
          <div className="float-right">
            <span className={book > 0 ? "book-green" : "book-red"}>{toCurrency(book)}</span>{" "}
            {rangeIcon(rangeId, item?.min, item?.max, 1, "\n                            ")}
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${open ? "" : " suspended"}`}
            onClick={() => open && item?.b && onBetClick?.(item.b, item.nat, "", item)}
          >
            <span className="casino-box-odd">{item?.b ?? ""}</span>
          </div>
        </div>
      </>
    );
  };

  const [playerA = {}, playerB = {}] = main;
  const [under = {}, over = {}] = underover;

  return (
    <div className="teen1daycasino-container d-none-small">
      <div className="teen1dayleft">
        {titleRow("playera", "Player A")} {mainRow(playerA, "A", "range1")}
      </div>{" "}
      <div className="teen1daycenter" />{" "}
      <div className="teen1dayright">
        {titleRow("playerb", "Player B")} {mainRow(playerB, "B", "range7")}
      </div>{" "}
      <div className="teen2uo">
        <div className="teen1dayleft" /> <div className="teen1daycenter" />{" "}
        <div className="teen1dayright">
          <div className="casino-box-row d-none-big">
            <div className="casino-nation-name no-border casino-bl-box-title">
              <div className="playerb">Player B</div>
            </div>{" "}
            <div className="casino-bl-box casino-bl-box-title" />
          </div>{" "}
          <div className="casino-box-row">
            {uoCells(under, "range5")} {uoCells(over, "range6")}
          </div>
        </div>
      </div>
    </div>
  );
}
